import * as Sentry from '@sentry/nextjs'
import { isAccessCodePagePath } from '@/lib/auth/customer/access-code-fragment'
import { redactSecretsInUrl } from './pii'

/**
 * 初めに開いた URL で、Sentry の Session Replay を始めてよいか（`instrumentation-client.ts` から呼ぶ）。
 *
 * 🔴 URL に秘密（はじめて用の URL の `#code=`・招待や再設定の入口トークン）があるページでは始めない
 *    （MYP-45・Codex SO 2026-09-29 HIGH）。Replay は録画の初めに `location.href` を meta イベントへそのまま入れ、
 *    `replay_event` の `urls` にも残す（`@sentry/replay` 10.75.0）。`beforeSend` は Replay を通らず、
 *    `beforeAddRecordingEvent` は custom イベントにしか効かないので、meta の href は送る前に伏せられない。
 *    画面があとで `history.replaceState` で「#」を消しても、録った値は残る。
 * 🔴 秘密かどうかの判定は `redactSecretsInUrl`（URL の秘密の正本）に任せる。形を2か所に持たない。
 *    伏せる前と後で文字列が変わる＝伏せるべき秘密を含む。
 *
 * 🔴 登録の画面（`/customer-portal/invite`）は、秘密が無くても録らない（Codex SO 2026-09-29 3周目 HIGH）。
 *    録画が動いていると、開いたまま「#code=」へ移ったときに、消す前の URL を録る入口が残るため。
 *
 * 失うもの: そのページを開いたときの録画だけ（登録・再設定の画面。つまずきは MYP-37 のエラーの口で追える）。
 */
export function shouldStartReplay(href: string): boolean {
  if (redactSecretsInUrl(href) !== href) return false
  return !isAccessCodePagePath(pathnameOf(href))
}

/** URL のパス。読めなければ空（空は登録の画面ではない＝上の判定に任せる）。 */
function pathnameOf(href: string): string {
  try {
    return new URL(href).pathname
  } catch (_notAUrl) {
    // 読めない値は、伏せ字の判定（上）だけで決める
    return ''
  }
}

/**
 * 登録の画面を開いたら、動いている Session Replay を止める（Codex SO 2026-09-29 4周目 LOW）。
 * ログイン画面などで始まった録画は、アプリの中の移動（「はじめての方はこちら」）で登録の画面へ来ても続く。
 * 初めから登録の画面を開いたときは `shouldStartReplay` が録り始めないので、どこから来ても登録の画面は録らない。
 */
export async function stopReplayOnAccessCodePage(): Promise<void> {
  await Sentry.getReplay()?.stop()
}
